/* @flow */
import type { ContextRouter } from 'react-router-dom';
import { createAction, handleActions, type ActionType } from 'redux-actions';
import * as F from 'lodash/fp';
import * as L from 'partial.lenses';
import * as React from 'react';

import { MediaTypeProvider } from '@sonyged/ext-proc';

import { Pizza } from '../../pizza/fields/domain/pizza';
import { PizzaBuilderView } from './PizzaBuilderView';
import { Rng } from '../../../utils/Random';
import { pizzaG, pizzaGameRules } from './game';
import AppContext from '../../../AppContext';
import CompletionPage from '../../common/CompletionPage';
import Menu from '../../ice/fields/Menu';
import PizzaAdvanceOrderView from './PizzaAdvanceOrderView';
import PizzaAdvanceResultView from './PizzaAdvanceResultView';
import PizzaAdvanceStatsView from './PizzaAdvanceStatsView';
import PizzaBakingView from '../../pizza/fields/PizzaBakingView';
import PizzaCookingView from '../../pizza/fields/PizzaCookingView';
import SimulationContent from './Simulation';

const ROUND_COUNT = 4;
const PIZZA_PRICE = 700;
const DEFAULT_CUSTOMER_COUNT = 100;
const DEFAULT_STAR = 2.5;

type Props = { ...ContextRouter };

type Screen =
  | { type: 'stats' | 'order' | 'build' | 'result' | 'cook' | 'bake' | 'simulation' | 'end' }
  | { type: 'historyResult', roundIndex: number };

let screenM = {
  ...F.pipe(
    F.map(type => [type, ({ type }: Screen)]),
    F.fromPairs
  )(['stats', 'order', 'build', 'result', 'cook', 'bake', 'simulation', 'end']),
  historyResult: roundIndex => ({ type: 'historyResult', roundIndex }),
};

type State = {
  firstPizza: Pizza,
  screen: Screen,
  pizzas: Pizza[],
  roundIndex: number,
};

let makeInitialState = (): State => ({
  firstPizza: pizzaG.gen(new Rng(Math.random())),
  screen: { type: 'stats' },
  pizzas: new Array(ROUND_COUNT),
  roundIndex: 0,
});

let next = createAction<'next', void>('next');
let back = createAction<'back', void>('back');
let showHistoryResult = createAction<'showHistoryResult', number>('showHistoryResult');
let setPizza = createAction<'setPizza', Pizza>('setPizza');

type Action =
  | ActionType<typeof next>
  | ActionType<typeof back>
  | ActionType<typeof showHistoryResult>
  | ActionType<typeof setPizza>;

let reducer = handleActions<State, _>(
  {
    [next.toString()]: state => {
      let { roundIndex, screen } = state;
      let type = screen.type;
      switch (type) {
        case 'stats':
          if (roundIndex >= ROUND_COUNT) return { ...state, screen: screenM.end };
          else return { ...state, screen: screenM.order };
        case 'order':
          return { ...state, screen: screenM.build };
        case 'build':
          return { ...state, screen: screenM.cook };
        case 'cook':
          return { ...state, screen: screenM.bake };
        case 'bake':
          return { ...state, screen: screenM.simulation };
        case 'simulation':
          return { ...state, screen: screenM.result };
        case 'result':
          return { ...state, screen: screenM.stats, roundIndex: roundIndex + 1 };
        case 'historyResult':
          return { ...state, screen: screenM.stats };
        case 'end':
          // can't go next
          return state;
        default:
          (type: empty);
          return state;
      }
    },

    [back.toString()]: state => {
      let {
        screen: { type },
      } = state;
      switch (type) {
        case 'build':
          return { ...state, screen: screenM.order };
        case 'cook':
          return { ...state, screen: screenM.build };
        case 'bake':
          return { ...state, screen: screenM.cook };
        case 'simulation':
          return { ...state, screen: screenM.bake };
        case 'result':
          return { ...state, screen: screenM.simulation };
        case 'order':
        case 'historyResult':
          return { ...state, screen: screenM.stats };
        case 'stats':
        case 'end':
          // can't go back
          return state;
        default:
          (type: empty);
          return state;
      }
    },

    [showHistoryResult.toString()]: (state, action: ActionType<typeof showHistoryResult>) => ({
      ...state,
      screen: screenM.historyResult(action.payload),
    }),

    [setPizza.toString()]: (state, action: ActionType<typeof setPizza>) => {
      let { roundIndex, pizzas } = state;
      let newPizzas = L.set(roundIndex, action.payload, pizzas);
      return { ...state, pizzas: newPizzas };
    },
  },
  (null: any)
);

function PizzaAdvanceFieldChallengePage(props: Props) {
  let [{ screen, pizzas, roundIndex, firstPizza }, dispatch] = React.useReducer<_, Action, _>(
    reducer,
    null,
    makeInitialState
  );
  let { soundController } = React.useContext<any>(AppContext);

  React.useEffect(() => {
    soundController.bgmPlay('proc_field03_pizza', true);
  }, []);

  let roundName = React.useMemo(() => roundIndex + 1 + '日目', [roundIndex]);

  let menuText;
  let screenType = screen.type;
  switch (screenType) {
    case 'stats':
    case 'order':
      menuText = '本日のピザを売ろう';
      break;
    case 'build':
    case 'simulation':
      menuText = roundName;
      break;
    case 'result':
    case 'historyResult':
      menuText = '結果' + roundName;
      break;
    case 'cook':
    case 'bake':
    case 'end':
      break;
    default:
      (screenType: empty);
      console.error(`unknown ${this.state.current.stage}`);
  }
  const showMenu = !F.includes(screen.type, ['cook', 'bake', 'end']);

  const [scores, stars, s] = React.useMemo(() => {
    let bakedPizzas = pizzas.slice(0, roundIndex + 1);
    let s = [];
    let scores = [];
    let stars = [];
    let prevPizza = firstPizza;
    let customerCount = DEFAULT_CUSTOMER_COUNT;
    let star = DEFAULT_STAR;
    for (let pizza of bakedPizzas) {
      if (!pizza) break;

      let { score, customerMultiplier, starDiff } = pizzaGameRules(pizza, prevPizza);
      customerCount = Math.round(customerCount * customerMultiplier);
      s.push(score);
      scores.push(customerCount * PIZZA_PRICE);
      star = star + starDiff;
      stars.push(star);
      prevPizza = pizza;
    }
    return [scores, stars, s];
  }, [pizzas, roundIndex]);

  let currentPizza = pizzas[roundIndex];
  let sale = scores[roundIndex];
  let star = stars[roundIndex];

  let prevPizza = React.useMemo(() => {
    if (roundIndex === 0) return firstPizza;
    return pizzas[roundIndex - 1];
  }, [roundIndex, firstPizza, pizzas]);

  let prevSale = scores[roundIndex - 1] || DEFAULT_CUSTOMER_COUNT * PIZZA_PRICE;
  let prevStar = stars[roundIndex - 1] || DEFAULT_STAR;

  let handleShowResult = React.useCallback(i => dispatch(showHistoryResult(i)), []);
  let handleNext = React.useCallback(() => dispatch(next()), []);
  let handleBack = React.useCallback(() => dispatch(back()), []);

  let currentScreen = null;
  switch (screen.type) {
    case 'stats':
      currentScreen = (
        <PizzaAdvanceStatsView
          scores={scores}
          stars={stars}
          pizzas={pizzas}
          roundIndex={roundIndex}
          showResult={handleShowResult}
          onNext={handleNext}
        />
      );
      break;
    case 'order':
      currentScreen = <PizzaAdvanceOrderView pizza={prevPizza} star={prevStar} sale={prevSale} onNext={handleNext} />;
      break;
    case 'build':
      currentScreen = (
        <PizzaBuilderView
          yesterdayPizza={prevPizza}
          yesterdaySale={prevSale}
          yesterdayStar={prevStar}
          onNext={pizza => {
            dispatch(setPizza(pizza));
            dispatch(next());
          }}
          onBack={handleBack}
        />
      );
      break;
    case 'cook':
      currentScreen = <PizzaCookingView round={{ pizza: currentPizza, customers: [], name: '' }} onNext={handleNext} />;
      break;
    case 'bake':
      currentScreen = <PizzaBakingView round={{ pizza: currentPizza, customers: [], name: '' }} onNext={handleNext} />;
      break;
    case 'simulation':
      currentScreen = (
        <SimulationContent
          onNext={handleNext}
          todayPizza={currentPizza}
          sale={sale}
          score={s[roundIndex]}
          customerCount={sale / PIZZA_PRICE}
          yesterdayCustomerCount={prevSale / PIZZA_PRICE}
        />
      );
      break;
    case 'result':
      currentScreen = (
        <PizzaAdvanceResultView
          sale={sale}
          customerCount={sale / PIZZA_PRICE}
          star={star}
          onNext={handleNext}
          yesterdayPizza={prevPizza}
          todayPizza={currentPizza}
        />
      );
      break;
    case 'historyResult':
      currentScreen = (
        <PizzaAdvanceResultView
          fromStats={true}
          sale={scores[screen.roundIndex]}
          customerCount={scores[screen.roundIndex] / PIZZA_PRICE}
          star={stars[screen.roundIndex]}
          onNext={handleNext}
          yesterdayPizza={pizzas[screen.roundIndex - 1] || firstPizza}
          todayPizza={pizzas[screen.roundIndex]}
        />
      );
      break;
    case 'end':
      currentScreen = <CompletionPage history={props.history} kind={'pizza-a'} totalScore={F.sum(scores)} />;
      break;
    default:
      (screen.type: empty);
  }

  return (
    <MediaTypeProvider>
      <div id="proc-pizza-fields-root-page" className="pizza proc-pizza-field-challenge">
        {showMenu && (
          <Menu
            exitLink={'/pizza-a/fields/'}
            query={'?skip_intro=true'}
            menuText={menuText}
            title={''}
            subTitle={'本日のピザを売ろう'}
            rootNodeId="proc-pizza-fields-root-page"
          />
        )}
        {currentScreen}
      </div>
    </MediaTypeProvider>
  );
}

export default PizzaAdvanceFieldChallengePage;
